/**
 * Home screen controller.
 * Renders the landing page with navigation buttons.
 */

import { el, clearChildren } from "../utils/dom";
import { BUILT_IN_THEMES } from "../../config/themes";
import { selectTheme, loadTheme } from "../theme";
import { button, toast } from "../components";
import { setCookie } from "../utils/cookies";
import { COOKIE_KEYS } from "../../config/constants";
import { downloadJson, pickFile, readFileAsText } from "../../data/backup";
import type { Theme } from "../../config/themes";

export type ScreenId =
  | "home"
  | "teambuilder"
  | "trainer"
  | "lobby-host"
  | "lobby-join"
  | "battle"
  | "bot-lobby"
  | "bot-battle"
  | "replay"
  | "spectate";

/** Render the home screen into the given container. */
export function renderHome(
  container: HTMLElement,
  navigate: (screen: ScreenId) => void,
): void {
  clearChildren(container);

  const header = el("header", { className: "app-header" });
  const title = el("h1", { text: "Pokemon Battle Simulator" });
  header.appendChild(title);

  // Theme switcher
  const themeSelect = el("select", {
    attrs: { "aria-label": "Theme" },
  }) as HTMLSelectElement;
  const currentTheme = loadTheme();
  for (const theme of BUILT_IN_THEMES) {
    const opt = el("option", { text: theme.name, attrs: { value: theme.id } });
    themeSelect.appendChild(opt);
  }
  themeSelect.value = currentTheme.id;
  themeSelect.addEventListener("change", () => {
    selectTheme(themeSelect.value);
  });
  header.appendChild(themeSelect);

  // Custom theme import/export
  const themeActions = el("div", { className: "actions-bar" });
  themeActions.appendChild(button("Export Theme", () => {
    const theme = loadTheme();
    downloadJson(theme, `${theme.id}.theme.json`);
  }, { variant: "secondary" }));
  themeActions.appendChild(button("Import Theme", async () => {
    const file = await pickFile("application/json");
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const theme = JSON.parse(text) as Theme;
      if (!theme.id || !theme.name || !theme.tokens) throw new Error("Invalid theme file");
      setCookie(COOKIE_KEYS.CUSTOM_THEME, theme);
      selectTheme(theme.id, [theme]);
      toast(`Theme "${theme.name}" imported!`, "success");
    } catch {
      toast("Failed to import theme", "error");
    }
  }, { variant: "secondary" }));
  header.appendChild(themeActions);

  const main = el("main", { className: "app-main" });
  const description = el("p", {
    text: "Battle with Generation 5 Pokemon over peer-to-peer connections. No accounts, no server.",
    className: "app-description",
  });
  main.appendChild(description);

  const buttons = [
    { label: "Host Battle", screen: "lobby-host" as ScreenId },
    { label: "Join Battle", screen: "lobby-join" as ScreenId },
    { label: "Practice vs Bot", screen: "bot-lobby" as ScreenId },
    { label: "Spectate", screen: "spectate" as ScreenId },
    { label: "Team Builder", screen: "teambuilder" as ScreenId },
    { label: "Trainer Profile", screen: "trainer" as ScreenId },
    { label: "Replays", screen: "replay" as ScreenId },
  ];

  const buttonGrid = el("div", { className: "button-grid" });
  for (const { label, screen } of buttons) {
    const btn = el("button", {
      text: label,
      className: "nav-button",
      onClick: () => navigate(screen),
    });
    buttonGrid.appendChild(btn);
  }
  main.appendChild(buttonGrid);

  container.appendChild(header);
  container.appendChild(main);
}
